import React, {ReactNode, useRef, useState} from 'react';
import cx from 'classnames';
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useInteractions,
    FloatingPortal,
    FloatingArrow,
    arrow,
    Placement
} from '@floating-ui/react';

import styles from './ModernTooltip.module.scss';

export type TooltipPosition = Placement;

export type TooltipProps = {
    wrapperClassname?: string;
    tooltip?: ReactNode;
    className?: string;
    children: ReactNode;
    delay?: number;
    zIndex?: number;
    position?: TooltipPosition;
    arrowOffset?: number;
    maxWidth?: number;
    tooltipOffset?: number;
    portalId?: string;
    show?: boolean;
    toggleShow?: (flag: boolean) => void;
};

export function ModernTooltip(props: TooltipProps) {
    const arrowRef = useRef();
    const {
        wrapperClassname,
        tooltip,
        children,
        className,
        zIndex,
        delay,
        position,
        arrowOffset,
        portalId,
        toggleShow,
        show,
        tooltipOffset = 5,
        maxWidth = 250
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const showTooltip = toggleShow ? show : isOpen;
    const {refs, floatingStyles, context} = useFloating({
        open: showTooltip,
        onOpenChange: toggleShow || setIsOpen,
        placement: position,
        strategy: 'fixed',

        // Make sure the tooltip stays on the screen
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(tooltipOffset),
            flip(),
            shift(),
            arrow({
                element: arrowRef
            })
        ]
    });

    // Event listeners to change the open state
    const hover = useHover(context, {move: false, delay: !isOpen ? delay : 0});
    const focus = useFocus(context);
    const dismiss = useDismiss(context);

    // Merge all the interactions into prop getters
    const {getReferenceProps, getFloatingProps} = useInteractions([hover, focus, dismiss]);

    const tooltipStyles = {...floatingStyles, zIndex, maxWidth};

    return (
        <>
            <div
                className={`tooltip-wrapper ${wrapperClassname}`}
                ref={refs.setReference}
                {...getReferenceProps()}
            >
                {children}
            </div>

            {showTooltip && (
                <FloatingPortal id={portalId}>
                    <div
                        className={cx(styles.tooltip, className)}
                        ref={refs.setFloating}
                        style={tooltipStyles}
                        {...getFloatingProps()}
                    >
                        {tooltip}
                        <FloatingArrow
                            width={9}
                            height={4}
                            ref={arrowRef}
                            context={context}
                            staticOffset={arrowOffset}
                        />
                    </div>
                </FloatingPortal>
            )}
        </>
    );
}
